import { useMemo } from 'react'
import type { LibraryMovieEntry, LibrarySeriesEntry } from './useLibrary'

export type MovieSortKey =
  | 'dateAddedDesc'
  | 'lastWatchedDesc'
  | 'userRatingDesc'
  | 'userRatingAsc'
  | 'tmdbRatingDesc'
  | 'tmdbRatingAsc'
  | 'titleAsc'
  | 'titleDesc'
  | 'yearDesc'
  | 'yearAsc'
  | 'runtimeDesc'
  | 'runtimeAsc'

export type SeriesSortKey =
  | 'lastUpdatedDesc'
  | 'userRatingDesc'
  | 'userRatingAsc'
  | 'tmdbRatingDesc'
  | 'tmdbRatingAsc'
  | 'watchedCountDesc'
  | 'titleAsc'
  | 'titleDesc'
  | 'yearDesc'
  | 'yearAsc'

/** Decade bucket labels used by the decade filter pills. */
export const DECADE_BUCKETS: { value: number; label: string; from: number; to: number }[] = [
  { value: 1970, label: '1970s', from: 1970, to: 1979 },
  { value: 1980, label: '1980s', from: 1980, to: 1989 },
  { value: 1990, label: '1990s', from: 1990, to: 1999 },
  { value: 2000, label: '2000s', from: 2000, to: 2009 },
  { value: 2010, label: '2010s', from: 2010, to: 2019 },
  { value: 2020, label: '2020s', from: 2020, to: 2029 },
]

/**
 * Mirrors desktop AdvancedFilterPanel / SearchFilters with extensions
 * the user explicitly requested. Every range field is optional.
 */
export interface LibraryFilters {
  source: 'all' | 'virtual' | 'physical'
  minUserRating: number
  maxUserRating: number
  minTmdbRating: number
  hasUserRating: boolean | undefined
  genres: string[]
  yearFrom: number | undefined
  yearTo: number | undefined
  runtimeFrom: number | undefined
  runtimeTo: number | undefined
  decades: number[]
  search: string
}

export const YEAR_MIN = 1900
export const YEAR_MAX = new Date().getFullYear()
export const RUNTIME_MIN = 0
export const RUNTIME_MAX = 300

export const DEFAULT_FILTERS: LibraryFilters = {
  source: 'all',
  minUserRating: 0,
  maxUserRating: 10,
  minTmdbRating: 0,
  hasUserRating: undefined,
  genres: [],
  yearFrom: undefined,
  yearTo: undefined,
  runtimeFrom: undefined,
  runtimeTo: undefined,
  decades: [],
  search: '',
}

export const DEFAULT_MOVIE_SORT: MovieSortKey = 'dateAddedDesc'
export const DEFAULT_SERIES_SORT: SeriesSortKey = 'lastUpdatedDesc'

export interface LibraryDetailsEntry {
  title: string
  year: number | null
  rating: number
  runtime: number | null
  genres: string[]
}

export function isFiltersDirty(filters: LibraryFilters): boolean {
  return (
    filters.source !== DEFAULT_FILTERS.source ||
    filters.minUserRating !== DEFAULT_FILTERS.minUserRating ||
    filters.maxUserRating !== DEFAULT_FILTERS.maxUserRating ||
    filters.minTmdbRating !== DEFAULT_FILTERS.minTmdbRating ||
    filters.hasUserRating !== DEFAULT_FILTERS.hasUserRating ||
    filters.genres.length > 0 ||
    filters.yearFrom !== undefined ||
    filters.yearTo !== undefined ||
    filters.runtimeFrom !== undefined ||
    filters.runtimeTo !== undefined ||
    filters.decades.length > 0 ||
    filters.search !== DEFAULT_FILTERS.search
  )
}

function effectiveYearBounds(filters: LibraryFilters): {
  from: number | undefined
  to: number | undefined
} {
  let from = filters.yearFrom
  let to = filters.yearTo
  if (filters.decades.length > 0) {
    const decadeFrom = Math.min(...filters.decades)
    const decadeTo = Math.max(...filters.decades.map((d) => d + 9))
    from = from === undefined ? decadeFrom : Math.max(from, decadeFrom)
    to = to === undefined ? decadeTo : Math.min(to, decadeTo)
  }
  return { from, to }
}

export function useFilteredSortedMovies(
  entries: LibraryMovieEntry[],
  sort: MovieSortKey,
  filters: LibraryFilters,
  detailsIndex?: Map<number, LibraryDetailsEntry>
): LibraryMovieEntry[] {
  return useMemo(() => {
    const needle = filters.search.trim().toLowerCase()
    const yearBounds = effectiveYearBounds(filters)
    const hasDecadeOrYear =
      filters.decades.length > 0 ||
      filters.yearFrom !== undefined ||
      filters.yearTo !== undefined
    const decadeSet = new Set(filters.decades)
    const genreSet = new Set(filters.genres.map((g) => g.toLowerCase()))

    const filtered = entries.filter((e) => {
      const r = e.record
      if (filters.source === 'virtual' && r.isVirtual === false) return false
      if (filters.source === 'physical' && r.isVirtual !== false) return false
      const userRating = typeof r.userRating === 'number' ? r.userRating : undefined
      if (filters.hasUserRating === true && userRating === undefined) return false
      if (filters.hasUserRating === false && userRating !== undefined) return false
      if (filters.minUserRating > 0) {
        if (userRating === undefined || userRating < filters.minUserRating) return false
      }
      if (filters.maxUserRating < 10) {
        if (userRating !== undefined && userRating > filters.maxUserRating) return false
      }

      const detail = detailsIndex?.get(e.tmdbId)

      if (filters.minTmdbRating > 0 && detail && detail.rating < filters.minTmdbRating) {
        return false
      }

      if (genreSet.size > 0 && detail) {
        const ok = detail.genres.some((g) => genreSet.has(g.toLowerCase()))
        if (!ok) return false
      }

      if (hasDecadeOrYear && detail && detail.year != null) {
        if (yearBounds.from !== undefined && detail.year < yearBounds.from) return false
        if (yearBounds.to !== undefined && detail.year > yearBounds.to) return false
        if (decadeSet.size > 0) {
          const bucket = Math.floor(detail.year / 10) * 10
          if (!decadeSet.has(bucket)) return false
        }
      }

      if (
        (filters.runtimeFrom !== undefined || filters.runtimeTo !== undefined) &&
        detail &&
        detail.runtime != null
      ) {
        if (filters.runtimeFrom !== undefined && detail.runtime < filters.runtimeFrom)
          return false
        if (filters.runtimeTo !== undefined && detail.runtime > filters.runtimeTo)
          return false
      }

      if (needle) {
        const title = detail?.title?.toLowerCase() || ''
        if (!title.includes(needle) && !String(e.tmdbId).includes(needle)) {
          return false
        }
      }
      return true
    })

    const sorted = [...filtered]
    sorted.sort((a, b) => compareMovies(a, b, sort, detailsIndex))
    return sorted
  }, [entries, sort, filters, detailsIndex])
}

export function useFilteredSortedSeries(
  entries: LibrarySeriesEntry[],
  sort: SeriesSortKey,
  filters: LibraryFilters,
  detailsIndex?: Map<number, LibraryDetailsEntry>
): LibrarySeriesEntry[] {
  return useMemo(() => {
    const needle = filters.search.trim().toLowerCase()
    const yearBounds = effectiveYearBounds(filters)
    const hasDecadeOrYear =
      filters.decades.length > 0 ||
      filters.yearFrom !== undefined ||
      filters.yearTo !== undefined
    const decadeSet = new Set(filters.decades)
    const genreSet = new Set(filters.genres.map((g) => g.toLowerCase()))

    const filtered = entries.filter((e) => {
      const r = e.record
      if (filters.source === 'virtual' && r.isVirtual === false) return false
      if (filters.source === 'physical' && r.isVirtual !== false) return false
      const userRating = typeof r.userRating === 'number' ? r.userRating : undefined
      if (filters.hasUserRating === true && userRating === undefined) return false
      if (filters.hasUserRating === false && userRating !== undefined) return false
      if (filters.minUserRating > 0) {
        if (userRating === undefined || userRating < filters.minUserRating) return false
      }
      if (filters.maxUserRating < 10) {
        if (userRating !== undefined && userRating > filters.maxUserRating) return false
      }

      const detail = detailsIndex?.get(e.tmdbId)

      if (filters.minTmdbRating > 0 && detail && detail.rating < filters.minTmdbRating) {
        return false
      }

      if (genreSet.size > 0 && detail) {
        const ok = detail.genres.some((g) => genreSet.has(g.toLowerCase()))
        if (!ok) return false
      }

      if (hasDecadeOrYear && detail && detail.year != null) {
        if (yearBounds.from !== undefined && detail.year < yearBounds.from) return false
        if (yearBounds.to !== undefined && detail.year > yearBounds.to) return false
        if (decadeSet.size > 0) {
          const bucket = Math.floor(detail.year / 10) * 10
          if (!decadeSet.has(bucket)) return false
        }
      }

      if (needle) {
        const title = detail?.title?.toLowerCase() || ''
        if (!title.includes(needle) && !String(e.tmdbId).includes(needle)) {
          return false
        }
      }
      return true
    })

    const sorted = [...filtered]
    sorted.sort((a, b) => compareSeries(a, b, sort, detailsIndex))
    return sorted
  }, [entries, sort, filters, detailsIndex])
}

function compareMovies(
  a: LibraryMovieEntry,
  b: LibraryMovieEntry,
  sort: MovieSortKey,
  index?: Map<number, LibraryDetailsEntry>
): number {
  switch (sort) {
    case 'dateAddedDesc':
      return parseDate(b.record.dateAdded) - parseDate(a.record.dateAdded)
    case 'lastWatchedDesc':
      return parseDate(b.record.lastWatched) - parseDate(a.record.lastWatched)
    case 'userRatingDesc': {
      const ra = typeof a.record.userRating === 'number' ? a.record.userRating : -1
      const rb = typeof b.record.userRating === 'number' ? b.record.userRating : -1
      return rb - ra
    }
    case 'userRatingAsc': {
      const ra = typeof a.record.userRating === 'number' ? a.record.userRating : 11
      const rb = typeof b.record.userRating === 'number' ? b.record.userRating : 11
      return ra - rb
    }
    case 'tmdbRatingDesc':
      return (index?.get(b.tmdbId)?.rating ?? 0) - (index?.get(a.tmdbId)?.rating ?? 0)
    case 'tmdbRatingAsc':
      return (index?.get(a.tmdbId)?.rating ?? 0) - (index?.get(b.tmdbId)?.rating ?? 0)
    case 'titleAsc': {
      const ta = index?.get(a.tmdbId)?.title ?? ''
      const tb = index?.get(b.tmdbId)?.title ?? ''
      return ta.localeCompare(tb)
    }
    case 'titleDesc': {
      const ta = index?.get(a.tmdbId)?.title ?? ''
      const tb = index?.get(b.tmdbId)?.title ?? ''
      return tb.localeCompare(ta)
    }
    case 'yearDesc':
      return (index?.get(b.tmdbId)?.year ?? 0) - (index?.get(a.tmdbId)?.year ?? 0)
    case 'yearAsc': {
      const ya = index?.get(a.tmdbId)?.year ?? 99999
      const yb = index?.get(b.tmdbId)?.year ?? 99999
      return ya - yb
    }
    case 'runtimeDesc':
      return (index?.get(b.tmdbId)?.runtime ?? 0) - (index?.get(a.tmdbId)?.runtime ?? 0)
    case 'runtimeAsc': {
      const ra = index?.get(a.tmdbId)?.runtime ?? 99999
      const rb = index?.get(b.tmdbId)?.runtime ?? 99999
      return ra - rb
    }
  }
}

function compareSeries(
  a: LibrarySeriesEntry,
  b: LibrarySeriesEntry,
  sort: SeriesSortKey,
  index?: Map<number, LibraryDetailsEntry>
): number {
  switch (sort) {
    case 'lastUpdatedDesc':
      return parseDate(b.record.lastUpdated) - parseDate(a.record.lastUpdated)
    case 'userRatingDesc': {
      const ra = typeof a.record.userRating === 'number' ? a.record.userRating : -1
      const rb = typeof b.record.userRating === 'number' ? b.record.userRating : -1
      return rb - ra
    }
    case 'userRatingAsc': {
      const ra = typeof a.record.userRating === 'number' ? a.record.userRating : 11
      const rb = typeof b.record.userRating === 'number' ? b.record.userRating : 11
      return ra - rb
    }
    case 'tmdbRatingDesc':
      return (index?.get(b.tmdbId)?.rating ?? 0) - (index?.get(a.tmdbId)?.rating ?? 0)
    case 'tmdbRatingAsc':
      return (index?.get(a.tmdbId)?.rating ?? 0) - (index?.get(b.tmdbId)?.rating ?? 0)
    case 'watchedCountDesc':
      return countWatched(b) - countWatched(a)
    case 'titleAsc': {
      const ta = index?.get(a.tmdbId)?.title ?? ''
      const tb = index?.get(b.tmdbId)?.title ?? ''
      return ta.localeCompare(tb)
    }
    case 'titleDesc': {
      const ta = index?.get(a.tmdbId)?.title ?? ''
      const tb = index?.get(b.tmdbId)?.title ?? ''
      return tb.localeCompare(ta)
    }
    case 'yearDesc':
      return (index?.get(b.tmdbId)?.year ?? 0) - (index?.get(a.tmdbId)?.year ?? 0)
    case 'yearAsc': {
      const ya = index?.get(a.tmdbId)?.year ?? 99999
      const yb = index?.get(b.tmdbId)?.year ?? 99999
      return ya - yb
    }
  }
}

/**
 * Series watched count for the "Most/Least watched" sort. Excludes
 * specials (season 0) so the sort matches the badge in `SeriesCard`
 * and TMDB's `numberOfEpisodes`, which also exclude bonus content.
 */
function countWatched(entry: LibrarySeriesEntry): number {
  let n = 0
  const eps = entry.record.episodes
  if (!eps) return 0
  for (const [seasonKey, season] of Object.entries(eps)) {
    if (!season) continue
    if (Number(seasonKey) === 0) continue
    for (const ep of Object.values(season)) {
      if (ep?.isWatched) n++
    }
  }
  return n
}

function parseDate(iso: string | null | undefined): number {
  if (!iso) return 0
  const t = Date.parse(iso)
  return isNaN(t) ? 0 : t
}
